import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAnimatedSidebar } from '@/components/motion/animated-sidebar';

import { useDocumentViewerDock } from './DocumentViewerContext';
import {
    dockAvailableWidth,
    dockWidth,
    readDockShare,
    shareAt,
    writeDockShare,
} from './dockLayout';
import './documentDock.css';

const DocumentViewerPanel = React.lazy(() => import('./DocumentViewerPanel'));

/** Ein Tastendruck auf dem Ziehgriff verschiebt um so viel vom Platz. */
const KEY_STEP = 0.05;

/**
 * Das Dokument als **rechte Spalte** der Shell, neben dem Chat statt über ihm.
 *
 * Als Overlay (920 px, fest) deckte der Betrachter den Chat zu: an einem 1728-px-Fenster blieben
 * vom Chat rund 480 px, und jede Antwort war rechts abgeschnitten; an einem 14-Zoll-Laptop rund
 * 200 px. Wer eine Angabe gegen das Original prüft, braucht beides nebeneinander (Sasse,
 * 06.10.2026). Deshalb, solange ein Dokument offen ist:
 *
 * - **Chat links, Dokument rechts**, getrennt von einem Ziehgriff; der Anteil bleibt im Browser.
 * - **Die Sidebar klappt zur Symbolleiste** und kommt beim Schließen zurück — aber nur, wenn sie
 *   vorher offen war.
 * - **Der Chat verliert seine Ränder** (`documentDock.css`, über `body[data-document-docked]`).
 *
 * Lebt in der `AnimatedSidebarProvider` der Shell, weil es die Sidebar steuert. Mobil meldet es
 * keinen Platz an; dort bleibt das Overlay des Providers.
 */
export default function DocumentViewerDock({ viewportWidth }: { viewportWidth: number }) {
    const { t } = useTranslation();
    const dock = useDocumentViewerDock();
    const { isMobile, open: sidebarOpen, setOpen: setSidebarOpen, width: sidebarWidth } = useAnimatedSidebar();

    const registerDock = dock?.registerDock;
    React.useEffect(() => (isMobile || !registerDock ? undefined : registerDock()), [isMobile, registerDock]);

    const shown = dock?.open && !isMobile ? dock.open : null;
    const docked = shown !== null;

    // Die Sidebar einklappen, solange das Dokument steht — den Zustand davor merken. Gelesen wird
    // er beim Andocken, nicht bei jeder Änderung: Klappt jemand sie selbst wieder auf, bleibt sie
    // nach dem Schließen offen.
    const sidebarOpenRef = React.useRef(sidebarOpen);
    sidebarOpenRef.current = sidebarOpen;
    React.useEffect(() => {
        if (!docked) return;
        const wasOpen = sidebarOpenRef.current;
        if (wasOpen) setSidebarOpen(false);
        document.body.dataset.documentDocked = 'true';
        return () => {
            delete document.body.dataset.documentDocked;
            if (wasOpen) setSidebarOpen(true);
        };
    }, [docked, setSidebarOpen]);

    const [share, setShare] = React.useState(readDockShare);
    const available = dockAvailableWidth(viewportWidth, sidebarOpen, sidebarWidth);
    const width = dockWidth(available, share);

    const [dragging, setDragging] = React.useState(false);
    const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
    };
    const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragging) return;
        setShare(shareAt(event.clientX, window.innerWidth, available));
    };
    const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragging) return;
        event.currentTarget.releasePointerCapture(event.pointerId);
        setDragging(false);
        writeDockShare(share);
    };
    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const delta = event.key === 'ArrowLeft' ? KEY_STEP : event.key === 'ArrowRight' ? -KEY_STEP : 0;
        if (!delta) return;
        event.preventDefault();
        const next = Math.min(0.85, Math.max(0.15, share + delta));
        setShare(next);
        writeDockShare(next);
    };

    if (!shown || !dock) return null;
    return (
        <aside
            className="document-viewer-dock sticky top-0 flex shrink-0 border-l bg-background"
            style={{ width }}
            data-testid="document-viewer-dock"
        >
            <div
                role="separator"
                aria-orientation="vertical"
                aria-label={t('documentViewer.resize')}
                aria-valuenow={Math.round(share * 100)}
                aria-valuemin={15}
                aria-valuemax={85}
                tabIndex={0}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={onKeyDown}
                className={`absolute inset-y-0 -left-1.5 z-20 w-3 cursor-col-resize touch-none outline-none after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 hover:after:bg-primary/40 focus-visible:after:bg-primary ${dragging ? 'after:bg-primary' : ''}`}
                data-testid="document-viewer-resize"
            />
            {/* Beim Ziehen: überall der Ziehcursor, und kein Text wird nebenbei markiert. */}
            {dragging && <div className="fixed inset-0 z-10 cursor-col-resize select-none" aria-hidden />}
            <React.Suspense fallback={null}>
                <DocumentViewerPanel
                    key={`${shown.request.fileId}:${shown.generation}`}
                    request={shown.request}
                    onClose={dock.close}
                    onOpenDocument={dock.openDocument}
                    docked
                />
            </React.Suspense>
        </aside>
    );
}
